import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
} from '@mui/material';
import { AgronodButton } from '@agronod/mui-components';
import {
  CalendarToday,
  MoreVert,
  Delete,
  CheckCircleOutlined,
  Add,
} from '@mui/icons-material';
import { Control } from '@/types';
import ControlStatusBadge, { getControlDisplayStatus } from './ControlStatusBadge';
import { isRenewable, renewableControlTypeId } from '@/utils/controls/renewal';
import NavKontrollerIcon from '@/components/icons/NavKontrollerIcon';

interface ControlProgramCardProps {
  control: Control;
  hasActiveShare?: boolean; // Override for share status (from parent's shares data)
  onViewDetails: () => void;
  onDelete?: (id: string) => void;
  onMarkCompleted?: (id: string) => void;
  onCreateNew?: (controlTypeId: string) => void;
  /** Förnya kontroll: offered for controls whose date has passed. */
  onRenew?: (control: Control) => void;
}

const ControlProgramCard: React.FC<ControlProgramCardProps> = ({
  control,
  hasActiveShare,
  onViewDetails,
  onDelete,
  onMarkCompleted,
  onCreateNew,
  onRenew
}) => {
  const isCompleted = control.status === 'COMPLETED';
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);
  const [logoFailed, setLogoFailed] = useState(false);
  // A new logoUrl deserves a fresh attempt — without this, a failed load
  // hides the logo permanently even after the URL is corrected.
  useEffect(() => {
    setLogoFailed(false);
  }, [control.issuer?.logoUrl]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(control.id);
    }
    handleMenuClose();
  };

  const handleMarkCompleted = () => {
    if (onMarkCompleted) {
      onMarkCompleted(control.id);
    }
    handleMenuClose();
  };

  // Calculate completion percentage using same logic as ControlDetailPage
  const calculateCompletionPercentage = () => {
    const documentRequirements = control.documentRequirements || [];
    if (documentRequirements.length === 0) return 0;

    const requirementStatuses = control.requirementStatuses || [];
    const confirmedDocuments = control.confirmedDocuments || [];

    // Separate required and optional
    const requiredRequirements = documentRequirements.filter(
      (req) => req.isRequired
    );
    const optionalRequirements = documentRequirements.filter(
      (req) => !req.isRequired
    );

    if (requiredRequirements.length === 0) return 100;

    // Calculate base total: required requirements (exclude NOT_APPLICABLE)
    const notApplicableRequiredCount = requiredRequirements.filter((req) => {
      const status = requirementStatuses.find(
        (s) => s.requirementId === req.id
      );
      return status?.verificationMethod === "NOT_APPLICABLE";
    }).length;

    let relevantTotal =
      requiredRequirements.length - notApplicableRequiredCount;

    // Count handled optional requirements (they should be included in total when handled)
    const handledOptionalCount = optionalRequirements.filter((req) => {
      const status = requirementStatuses.find(
        (s) => s.requirementId === req.id
      );

      // Skip not applicable optional
      if (status?.verificationMethod === "NOT_APPLICABLE") return false;

      // Count as handled if checked on farm
      if (status?.verificationMethod === "CHECKED_ON_FARM") return true;

      // Count as handled if document uploaded
      return confirmedDocuments.some((doc) => doc.requirementId === req.id);
    }).length;

    // Add handled optional to total
    relevantTotal += handledOptionalCount;

    if (relevantTotal === 0) return 100; // All requirements are not applicable

    // Count handled required requirements
    const handledRequiredCount = requiredRequirements.filter((req) => {
      const status = requirementStatuses.find(
        (s) => s.requirementId === req.id
      );

      // Skip not applicable
      if (status?.verificationMethod === "NOT_APPLICABLE") return false;

      // Count as handled if checked on farm
      if (status?.verificationMethod === "CHECKED_ON_FARM") return true;

      // Count as handled if document uploaded
      return confirmedDocuments.some((doc) => doc.requirementId === req.id);
    }).length;

    const totalHandled = handledRequiredCount + handledOptionalCount;

    return Math.round((totalHandled / relevantTotal) * 100);
  };

  const progressPercentage = calculateCompletionPercentage();

  // Calculate counts for display - matching ControlDetailPage logic
  const documentRequirements = control.documentRequirements || [];
  const requirementStatuses = control.requirementStatuses || [];
  const confirmedDocuments = control.confirmedDocuments || [];

  // Separate required and optional
  const requiredRequirements = documentRequirements.filter((req) => req.isRequired);
  const optionalRequirements = documentRequirements.filter((req) => !req.isRequired);

  // Base total: required (exclude NOT_APPLICABLE)
  const notApplicableCount = requiredRequirements.filter((req) => {
    const status = requirementStatuses.find((s) => s.requirementId === req.id);
    return status?.verificationMethod === "NOT_APPLICABLE";
  }).length;

  let totalDocs = requiredRequirements.length - notApplicableCount;

  // Add handled optional to total
  const handledOptionalCount = optionalRequirements.filter((req) => {
    const status = requirementStatuses.find((s) => s.requirementId === req.id);
    if (status?.verificationMethod === "NOT_APPLICABLE") return false;
    if (status?.verificationMethod === "CHECKED_ON_FARM") return true;
    return confirmedDocuments.some((doc) => doc.requirementId === req.id);
  }).length;

  totalDocs += handledOptionalCount;

  // Count uploaded documents (both required and handled optional, excluding NOT_APPLICABLE and CHECKED_ON_FARM)
  const uploadedDocs = documentRequirements.filter((req) => {
    const status = requirementStatuses.find((s) => s.requirementId === req.id);
    // Don't count NOT_APPLICABLE or CHECKED_ON_FARM
    if (
      status?.verificationMethod === "NOT_APPLICABLE" ||
      status?.verificationMethod === "CHECKED_ON_FARM"
    )
      return false;
    // Count if document uploaded
    const hasDocument = confirmedDocuments.some((doc) => doc.requirementId === req.id);
    // For optional: only count if document exists (i.e., it's been handled)
    if (!req.isRequired) return hasDocument;
    // For required: count if document exists
    return hasDocument;
  }).length;

  // Count checked on farm (both required and optional if marked)
  const checkedOnFarmDocs = documentRequirements.filter((req) => {
    const status = requirementStatuses.find((s) => s.requirementId === req.id);
    return status?.verificationMethod === "CHECKED_ON_FARM";
  }).length;

  const handledDocs = uploadedDocs + checkedOnFarmDocs;

  // Format dates
  const formatDate = (date?: string) => {
    if (!date) return 'Ej schemalagd';
    return new Date(date).toLocaleDateString('sv-SE', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Get display status for status badge
  // Use hasActiveShare prop as override if provided (frontend-determined share status)
  const statusInfo = getControlDisplayStatus(control);
  const displayStatus = hasActiveShare ? 'SHARED' : statusInfo.status;
  const statusMissingCount = statusInfo.missingCount;

  // Get button text based on progress
  const getButtonText = () => {
    if (progressPercentage === 100) {
      return 'Granska kontroll';
    } else if (progressPercentage > 0) {
      return 'Hantera kontroll';
    } else {
      return 'Förbered kontroll';
    }
  };

  // "Lägg till ny" is offered as soon as the control's date has passed, not
  // only once it is marked as completed: the next year's control is what the
  // farmer wants then, and marking the old one first was an extra step nobody
  // knew about. A renewable control prefills the create page with the type,
  // a bumped name and a date twelve months on; a completed control without a
  // date can still start from its type alone.
  const offersNew = isCompleted || isRenewable(control);
  const handleCreateNew = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRenew && isRenewable(control)) {
      onRenew(control);
    } else if (onCreateNew) {
      onCreateNew(renewableControlTypeId(control) ?? '');
    }
  };


  // Determine next control date
  const nextControlDate = control.scheduledDate ? new Date(control.scheduledDate) : null;

  return (
    <Card
      variant="outlined"
      onClick={() => {
        // Don't navigate if menu is open
        if (!menuOpen) {
          onViewDetails();
        }
      }}
      sx={{
        borderRadius: '16px',
        borderColor: 'rgba(0, 0, 0, 0.12)',
        cursor: 'pointer',
        boxShadow: 'none',
        p: { xs: 2, sm: 3 },
        '&:hover': {
          borderColor: 'secondary.main',
        },
        transition: 'all 0.2s',
      }}
    >
      <Box sx={{ position: 'relative' }}>
        {/* Three-dot menu - positioned absolutely in top right on mobile only */}
        <Box
          sx={{
            display: { xs: 'block', sm: 'none' },
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{
              width: 36,
              height: 36,
              borderRadius: '16px',
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'grey.100',
              },
            }}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          flexDirection: { xs: 'column', sm: 'row' },
        }}>
          {/* LEFT: Icon and Title section */}
          <Box sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start',
            flex: 1,
            minWidth: 0,
            width: { xs: '100%', sm: 'auto' },
            pr: { xs: 5, sm: 0 }, // Padding right on mobile to avoid three-dot menu
          }}>
            {/* Control Type Icon or Logo — framed in a white circle so issuer
                logos (some with their own coloured background) read cleanly. */}
            {control.issuer?.logoUrl && !logoFailed ? (
              <Box
                sx={{
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  borderRadius: '50%',
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  overflow: 'hidden',
                  p: 0.5,
                }}
              >
                <Box
                  component="img"
                  src={control.issuer.logoUrl}
                  alt={control.issuer.displayName || 'Utfärdarlogga'}
                  onError={() => setLogoFailed(true)}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  borderRadius: '50%',
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <NavKontrollerIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: 'hsla(45, 3%, 27%, 1)' }} />
              </Box>
            )}

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Title and category */}
              <Box sx={{ mb: { xs: 1, sm: 1.5 } }}>
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontWeight: 500,
                    fontSize: { xs: '15px', sm: '16px' },
                    lineHeight: { xs: '22px', sm: '24px' },
                    color: 'text.primary',
                    mb: 0.5,
                  }}
                >
                  {control.name}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: { xs: '14px', sm: '16px' },
                    lineHeight: { xs: '20px', sm: '24px' },
                    color: 'text.secondary',
                  }}
                >
                  {control.issuer?.displayName || 'Okänd utfärdare'}
                </Typography>
              </Box>

              {/* Metadata row */}
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: { xs: 1, sm: 1.5 }, alignItems: 'center' }}>
                <CalendarToday sx={{ fontSize: { xs: 14, sm: 16 }, color: 'text.secondary' }} />
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: { xs: '14px', sm: '16px' },
                    lineHeight: { xs: '20px', sm: '24px' },
                    color: 'text.secondary',
                  }}
                >
                  Datum:
                </Typography>
                {nextControlDate ? (
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      fontSize: { xs: '14px', sm: '16px' },
                      lineHeight: { xs: '20px', sm: '24px' },
                      color: 'text.secondary',
                    }}
                  >
                    {formatDate(control.scheduledDate!)}
                  </Typography>
                ) : (
                  <Chip
                    label="Ej tillagt"
                    size="small"
                    sx={{
                      bgcolor: 'grey.100',
                      border: 'none',
                      color: 'text.secondary',
                      fontFamily: 'Inter',
                      fontWeight: 500,
                      fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                      height: { xs: 28, sm: 32 },
                      borderRadius: '50px',
                      px: 0.5,
                      '& .MuiChip-label': {
                        px: 1.5,
                      },
                    }}
                  />
                )}
              </Box>

              {/* Progress bar with status badge */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                <Box
                  sx={{
                    flex: 1,
                    height: { xs: 6, sm: 8 },
                    borderRadius: '33554400px',
                    bgcolor: progressPercentage === 100 ? 'success.medium' : 'grey.100',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {progressPercentage > 0 && progressPercentage < 100 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: `${progressPercentage}%`,
                        bgcolor: 'primary.main',
                        borderRadius: '33554400px',
                      }}
                    />
                  )}
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: { xs: '14px', sm: '16px' },
                    lineHeight: { xs: '20px', sm: '24px' },
                    color: 'text.secondary',
                    minWidth: { xs: 75, sm: 95 },
                  }}
                >
                  {handledDocs}/{totalDocs} ({progressPercentage}%)
                </Typography>
                {/* Status badge - desktop only */}
                {!isCompleted && (
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <ControlStatusBadge status={displayStatus} missingCount={statusMissingCount} size="small" />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Status badge - mobile only, above button */}
          {!isCompleted && (
            <Box sx={{ display: { xs: 'block', sm: 'none' }, mb: 1.5, pl: 0 }}>
              <ControlStatusBadge status={displayStatus} missingCount={statusMissingCount} size="small" />
            </Box>
          )}

          {/* RIGHT: Action buttons */}
          {/* On a phone the two buttons share the row while they fit and
              otherwise wrap to one full-width row each; nowrap text in two
              flex:1 buttons clipped "Hantera kontroll" at 360 px. */}
          <Box sx={{
            display: 'flex',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: 1,
            alignItems: 'center',
            width: { xs: '100%', sm: 'auto' },
            marginLeft: { xs: 0, sm: 'auto' },
          }}>
              {offersNew ? (
                <>
                  {/* Content-sized on desktop ('0 0 auto', not 0): a zero basis
                      let the buttons shrink below the width the row reserved
                      for them, leaving a gap before the three-dot menu. */}
                  <AgronodButton
                    variant="contained"
                    startIcon={<Add sx={{ fontSize: 16 }} />}
                    onClick={handleCreateNew}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      borderRadius: '24px',
                      px: 2.5,
                      py: 1.25,
                      fontSize: '16px',
                      lineHeight: '24px',
                      height: '44px',
                      flex: { xs: '1 1 150px', sm: '0 0 auto' },
                      minWidth: { xs: 'auto', sm: '140px' },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Lägg till ny
                  </AgronodButton>
                  {/* The way into this control stays, now as the secondary
                      action. "Förbered" beside "Lägg till ny" told the farmer
                      to start on a control whose date has passed; with nothing
                      begun it is just a view. */}
                  <AgronodButton
                    variant="outlined"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      onViewDetails();
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      borderRadius: '24px',
                      px: 2.5,
                      py: 1.25,
                      fontSize: '16px',
                      lineHeight: '24px',
                      height: '44px',
                      flex: { xs: '1 1 150px', sm: '0 0 auto' },
                      minWidth: { xs: 'auto', sm: '140px' },
                      whiteSpace: 'nowrap',
                      borderColor: 'grey.300',
                      color: 'text.primary',
                      '&:hover': {
                        borderColor: 'grey.400',
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    {isCompleted || progressPercentage === 0 ? 'Visa kontroll' : getButtonText()}
                  </AgronodButton>
                </>
              ) : (
                <AgronodButton
                  variant="contained"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onViewDetails();
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: '24px',
                    px: 2.5,
                    py: 1.25,
                    fontSize: '16px',
                    lineHeight: '24px',
                    height: '44px',
                    width: { xs: '100%', sm: 'auto' },
                    minWidth: { xs: 'auto', sm: '160px' },
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getButtonText()}
                </AgronodButton>
              )}

              {/* Three-dot menu - on desktop only, pushed to the far right */}
              <Box
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  marginLeft: 'auto',
                }}
              >
                <IconButton
                  size="small"
                  onClick={handleMenuOpen}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '16px',
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: 'grey.100',
                    },
                  }}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
              </Box>
          </Box>
        </Box>
      </Box>

      {/* Three-dot menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 200,
            },
          },
        }}
      >
        {control.status !== 'COMPLETED' && onMarkCompleted && (
          <MenuItem
            onClick={handleMarkCompleted}
            sx={{
              height: 48,
              gap: 1,
            }}
          >
            <CheckCircleOutlined fontSize="small" sx={{ color: 'success.main' }} />
            <ListItemText>Markera som genomförd</ListItemText>
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem
            onClick={handleDelete}
            sx={{
              color: 'error.main',
              height: 48,
              gap: 1,
            }}
          >
            <Delete fontSize="small" sx={{ color: 'error.main' }} />
            <ListItemText>Ta bort</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Card>
  );
};

export default ControlProgramCard;
